import React, { useState, useEffect, useContext } from 'react';
import { Container, Card, Table, Button, Form, InputGroup, Badge, ProgressBar, Modal, Row, Col } from 'react-bootstrap';
import { FaPlus, FaSearch, FaEdit, FaTrash, FaProjectDiagram, FaEye, FaCalendar, FaMoneyBillWave, FaUserPlus, FaUsers, FaClock } from 'react-icons/fa';
import { AuthContext } from '../context/AuthContext';
import projectService from '../services/projectService';
import userService from '../services/userService';
import { formatCurrency, formatDate, formatNumber } from '../utils/format';
import LoadingSpinner from '../components/LoadingSpinner';
import { isViewer, canModifyData } from '../utils/rbac';

// Helper Components
const ProjectStatusBadge = ({ status }) => {
  const statusMap = {
    planning: { label: 'تخطيط', class: 'status-new' },
    in_progress: { label: 'قيد التنفيذ', class: 'status-in-progress' },
    on_hold: { label: 'معلق', class: 'status-on-hold' },
    completed: { label: 'مكتمل', class: 'status-resolved' }
  };
  const config = statusMap[status] || { label: status, class: 'status-on-hold' };
  return <span className={`status-badge ${config.class}`}>{config.label}</span>;
};

const ProjectTypeBadge = ({ type }) => {
  const typeMap = {
    network: { label: 'شبكات', color: '#667eea' },
    cameras: { label: 'كاميرات', color: '#10b981' },
    systems: { label: 'أنظمة', color: '#f59e0b' },
    infrastructure: { label: 'بنية تحتية', color: '#ef4444' },
    other: { label: 'أخرى', color: '#64748b' }
  };
  const config = typeMap[type] || { label: type, color: '#64748b' };
  return (
    <span 
      className="badge"
      style={{ 
        background: `${config.color}20`, 
        color: config.color,
        fontWeight: 600
      }}
    >
      {config.label}
    </span>
  );
};

const ProjectList = () => {
  const { user } = useContext(AuthContext);
  const userRole = user?.role || '';
  const canEdit = canModifyData(userRole) && !isViewer(userRole);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [projectType, setProjectType] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [users, setUsers] = useState([]);
  
  // Form state
  const [formData, setFormData] = useState({
    project_name: '',
    client_name: '',
    project_type: '',
    start_date: '',
    end_date: '',
    budget: '',
    status: 'planning',
    description: '',
    objectives: '',
    department: '',
    location: '',
    manager_id: '',
    manager2_id: '',
    assistants: []
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchProjects();
    fetchUsers();
  }, []);

  useEffect(() => {
    fetchProjects();
  }, [status, projectType]);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const data = await projectService.getProjects({ 
        search, 
        status, 
        project_type: projectType 
      });
      setProjects(data.projects || []);
    } catch (error) {
      console.error('Error fetching projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const data = await userService.getAll();
      setUsers(data.users || data || []);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchProjects();
  };

  const handleDelete = async (id) => {
    if (window.confirm('هل أنت متأكد من حذف هذا المشروع؟')) {
      try {
        await projectService.delete(id);
        fetchProjects();
      } catch (error) {
        alert('فشل حذف المشروع');
      }
    }
  };

  const openAddModal = () => {
    setEditingProject(null);
    setFormData({
      project_name: '',
      client_name: '',
      project_type: '',
      start_date: '',
      end_date: '',
      budget: '',
      status: 'planning',
      description: '',
      objectives: '',
      department: '',
      location: '',
      manager_id: '',
      manager2_id: '',
      assistants: []
    });
    setShowModal(true);
  };

  const openEditModal = (project) => {
    setEditingProject(project);
    setFormData({
      project_name: project.project_name || '',
      client_name: project.client_name || '',
      project_type: project.project_type || '',
      start_date: project.start_date ? project.start_date.split('T')[0] : '',
      end_date: project.end_date ? project.end_date.split('T')[0] : '',
      budget: project.budget || '',
      status: project.status || 'planning',
      description: project.description || '',
      objectives: project.objectives || '',
      department: project.department || '',
      location: project.location || '',
      manager_id: project.manager_id || '',
      manager2_id: project.manager2_id || '',
      assistants: project.assistants || []
    });
    setShowModal(true);
  };

  const viewProject = (project) => {
    setSelectedProject(project);
    setShowViewModal(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    // Validate required fields
    if (!formData.project_name?.trim()) {
      alert('اسم المشروع مطلوب');
      return;
    }
    if (!formData.project_type?.trim()) {
      alert('نوع المشروع مطلوب');
      return;
    }
    if (!formData.start_date?.trim()) {
      alert('تاريخ البداية مطلوب');
      return;
    }
    if (!formData.end_date?.trim()) {
      alert('تاريخ النهاية مطلوب');
      return;
    }
    if (!formData.manager_id?.trim()) {
      alert('مدير المشروع مطلوب');
      return;
    }
    if (!formData.client_name?.trim()) {
      alert('اسم العميل مطلوب');
      return;
    }
    if (!formData.department?.trim()) {
      alert('القسم مطلوب');
      return;
    }
    if (!formData.location?.trim()) {
      alert('الموقع مطلوب');
      return;
    }
    if (!formData.description?.trim()) {
      alert('وصف المشروع مطلوب');
      return;
    }
    if (!formData.objectives?.trim()) {
      alert('أهداف المشروع مطلوبة');
      return;
    }

    try {
      setSaving(true);
      if (editingProject) {
        await projectService.update(editingProject.id, formData);
      } else {
        await projectService.create(formData);
      }
      setShowModal(false);
      fetchProjects();
    } catch (error) {
      alert(error.response?.data?.error || 'فشل حفظ المشروع');
    } finally {
      setSaving(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      planning: 'info',
      in_progress: 'primary',
      on_hold: 'warning',
      completed: 'success',
      cancelled: 'danger'
    };
    return badges[status] || 'secondary';
  };

  const getStatusText = (status) => {
    const texts = {
      planning: 'تخطيط',
      in_progress: 'قيد التنفيذ',
      on_hold: 'معلق',
      completed: 'مكتمل',
      cancelled: 'ملغى'
    };
    return texts[status] || status;
  };

  const getTypeBadge = (type) => {
    const badges = {
      network: 'primary',
      cameras: 'success',
      systems: 'info',
      infrastructure: 'warning',
      other: 'secondary'
    };
    return badges[type] || 'secondary';
  };

  const getTypeText = (type) => {
    const texts = {
      network: 'شبكات',
      cameras: 'كاميرات',
      systems: 'أنظمة',
      infrastructure: 'بنية تحتية',
      other: 'أخرى'
    };
    return texts[type] || type || '-';
  };

  return (
    <Container fluid className="page-container project-list-container">
      {/* Page Header */}
      <div className="dashboard-header">
        <div className="dashboard-header-content">
          <div className="dashboard-header-icon">
            <FaProjectDiagram />
          </div>
          <div>
            <h1 className="dashboard-header-title">المشاريع</h1>
            <p className="dashboard-header-subtitle">إدارة ومتابعة جميع المشاريع ({projects.length} مشروع)</p>
          </div>
        </div>
        <div className="page-actions">
          {canEdit && (
            <Button className="btn-create" onClick={openAddModal}>
              <FaPlus /> مشروع جديد
            </Button>
          )}
        </div>
      </div>

      {/* Filter Card */}
      <Card className="dashboard-card mb-4">
        <Card.Body>
          <Row className="g-3 align-items-end">
            <Col md={6} lg={4}>
              <Form.Group className="mb-0">
                <Form.Label className="filter-label">البحث</Form.Label>
                <div className="search-wrapper">
                  <span className="search-icon"><FaSearch /></span>
                  <Form.Control
                    type="text"
                    placeholder="بحث بالاسم أو الكود أو العميل..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="search-input"
                  />
                </div>
              </Form.Group>
            </Col>
            <Col md={3} lg={2}>
              <Form.Group className="mb-0">
                <Form.Label className="filter-label">الحالة</Form.Label>
                <Form.Select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  className="form-select-custom"
                >
                  <option value="">الكل</option>
                  <option value="planning">تخطيط</option>
                  <option value="in_progress">قيد التنفيذ</option>
                  <option value="on_hold">معلق</option>
                  <option value="completed">مكتمل</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3} lg={2}>
              <Form.Group className="mb-0">
                <Form.Label className="filter-label">النوع</Form.Label>
                <Form.Select
                  value={projectType}
                  onChange={(e) => setProjectType(e.target.value)}
                  className="form-select-custom"
                >
                  <option value="">الكل</option>
                  <option value="network">شبكات</option>
                  <option value="cameras">كاميرات</option>
                  <option value="systems">أنظمة</option>
                  <option value="infrastructure">بنية تحتية</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Projects Table */}
      <Card className="dashboard-card">
        <Card.Body className="p-0">
          {loading ? (
            <div className="p-4">
              <div className="skeleton skeleton-card"></div>
            </div>
          ) : (
            <div className="table-responsive-wrapper responsive-table-wrapper">
              <table className="modern-table responsive-data-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>كود المشروع</th>
                    <th>اسم المشروع</th>
                    <th>العميل</th>
                    <th>النوع</th>
                    <th>الميزانية</th>
                    <th>التنفيذ</th>
                    <th>الحالة</th>
                    <th>تاريخ الانتهاء</th>
                    <th className="text-center">إجراء</th>
                  </tr>
                </thead>
                <tbody>
                  {projects.length === 0 ? (
                    <tr>
                      <td colSpan="10">
                        <div className="table-empty-state">
                          <div className="table-empty-state-icon">
                            <FaProjectDiagram size={48} />
                          </div>
                          <h5 className="empty-state-title">لا توجد مشاريع</h5>
                          <p className="empty-state-description">
                            لم يتم إنشاء أي مشاريع بعد
                          </p>
                          {canEdit && (
                            <Button className="btn-create" onClick={openAddModal}>
                              <FaPlus /> إنشاء مشروع
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ) : (
                    projects.map((project, index) => (
                      <tr key={project.id}>
                        <td data-label="#"><span className="text-muted">{index + 1}</span></td>
                        <td data-label="كود المشروع">
                          <span className="cell-link">{project.project_code}</span>
                        </td>
                        <td data-label="اسم المشروع">
                          <span className="fw-medium">{project.project_name}</span>
                        </td>
                        <td data-label="العميل">{project.client_name || '-'}</td>
                        <td data-label="النوع">
                          <ProjectTypeBadge type={project.project_type} />
                        </td>
                        <td data-label="الميزانية">
                          <span className="text-success fw-bold">{formatCurrency(project.budget)}</span>
                        </td>
                        <td data-label="التنفيذ" style={{ width: '140px' }}>
                          <div className="widget-progress-container">
                            <div
                              className="widget-progress-bar"
                              style={{
                                width: `${project.progress || 0}%`,
                                background: project.progress >= 100 ? 'var(--color-success)' : project.progress >= 50 ? 'var(--brand-primary)' : 'var(--color-warning)'
                              }}
                            />
                          </div>
                            <span className="text-muted" style={{ fontSize: '0.75rem' }}>{formatNumber(project.progress || 0)}%</span>
                        </td>
                        <td data-label="الحالة">
                          <ProjectStatusBadge status={project.status} />
                        </td>
                        <td data-label="تاريخ الانتهاء">
                          <span className="text-muted">
                            {project.end_date ? formatDate(project.end_date) : '-'}
                          </span>
                        </td>
                        <td data-label="إجراء" className="table-cell-actions">
                          <div className="table-actions">
                            <Button
                              className="btn-table-action"
                              onClick={() => viewProject(project)}
                            >
                              <FaEye size={14} />
                            </Button>
                            <Button
                              className="btn-table-action btn-table-action-success"
                              onClick={() => openEditModal(project)}
                            >
                              <FaEdit size={14} />
                            </Button>
                            <Button
                              className="btn-table-action btn-table-action-danger"
                              onClick={() => handleDelete(project.id)}
                            >
                              <FaTrash size={14} />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Add/Edit Project Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{editingProject ? 'تعديل المشروع' : 'مشروع جديد'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row>
            <Col md={8} className="mb-3">
              <Form.Label>اسم المشروع *</Form.Label>
              <Form.Control 
                type="text" 
                name="project_name"
                value={formData.project_name}
                onChange={handleInputChange}
                placeholder="اسم المشروع" 
                required 
              />
            </Col>
            <Col md={4} className="mb-3">
              <Form.Label>كود المشروع</Form.Label>
              <Form.Control 
                type="text" 
                value={editingProject?.project_code || 'سيتم توليده تلقائياً'} 
                disabled 
              />
            </Col>
            <Col md={6} className="mb-3">
              <Form.Label>العميل *</Form.Label>
              <Form.Control 
                type="text" 
                name="client_name"
                value={formData.client_name}
                onChange={handleInputChange}
                placeholder="اسم العميل" 
                required
              />
            </Col>
            <Col md={6} className="mb-3">
              <Form.Label>نوع المشروع *</Form.Label>
              <Form.Select 
                name="project_type"
                value={formData.project_type}
                onChange={handleInputChange}
                required
              >
                <option value="">اختر النوع</option>
                <option value="network">شبكات</option>
                <option value="cameras">كاميرات مراقبة</option>
                <option value="systems">أنظمة برمجية</option>
                <option value="infrastructure">بنية تحتية</option>
                <option value="other">أخرى</option>
              </Form.Select>
            </Col>
            <Col md={6} className="mb-3">
              <Form.Label>تاريخ البداية *</Form.Label>
              <Form.Control 
                type="date" 
                name="start_date"
                value={formData.start_date}
                onChange={handleInputChange}
                required
              />
            </Col>
            <Col md={6} className="mb-3">
              <Form.Label>تاريخ النهاية *</Form.Label>
              <Form.Control 
                type="date" 
                name="end_date"
                value={formData.end_date}
                onChange={handleInputChange}
                required
              />
            </Col>
            <Col md={6} className="mb-3">
              <Form.Label>الميزانية</Form.Label>
              <Form.Control 
                type="number" 
                name="budget"
                value={formData.budget}
                onChange={handleInputChange}
                placeholder="الميزانية" 
              />
            </Col>
            <Col md={6} className="mb-3">
              <Form.Label>الحالة</Form.Label>
              <Form.Select 
                name="status"
                value={formData.status}
                onChange={handleInputChange}
              >
                <option value="planning">تخطيط</option>
                <option value="in_progress">قيد التنفيذ</option>
                <option value="on_hold">معلق</option>
                <option value="completed">مكتمل</option>
              </Form.Select>
            </Col>
            <Col md={6} className="mb-3">
              <Form.Label>القسم *</Form.Label>
              <Form.Control 
                type="text" 
                name="department"
                value={formData.department}
                onChange={handleInputChange}
                placeholder="القسم" 
                required
              />
            </Col>
            <Col md={6} className="mb-3">
              <Form.Label>الموقع *</Form.Label>
              <Form.Control 
                type="text" 
                name="location"
                value={formData.location}
                onChange={handleInputChange}
                placeholder="الموقع" 
                required
              />
            </Col>
            <Col md={6} className="mb-3">
              <Form.Label>مدير المشروع *</Form.Label>
              <Form.Select 
                name="manager_id"
                value={formData.manager_id}
                onChange={handleInputChange}
                required
              >
                <option value="">اختر المدير</option>
                {users.map(user => (
                  <option key={user.id} value={user.id}>{user.full_name} ({user.username})</option>
                ))}
              </Form.Select>
            </Col>
            <Col md={6} className="mb-3">
              <Form.Label>مدير المشروع (بديل)</Form.Label>
              <Form.Select 
                name="manager2_id"
                value={formData.manager2_id}
                onChange={handleInputChange}
              >
                <option value="">اختر المدير البديل (اختياري)</option>
                {users.map(user => (
                  <option key={user.id} value={user.id}>{user.full_name} ({user.username})</option>
                ))}
              </Form.Select>
            </Col>
            <Col md={12} className="mb-3">
              <Form.Label>مساعدين المشروع</Form.Label>
              <div className="border rounded p-3 bg-light" style={{ maxHeight: '150px', overflowY: 'auto' }}>
                {users.length > 0 ? (
                  users.map(user => (
                    <Form.Check
                      key={user.id}
                      type="checkbox"
                      id={`assistant-${user.id}`}
                      label={`${user.full_name} (${user.username})`}
                      checked={formData.assistants?.includes(user.id)}
                      onChange={(e) => {
                        const userId = user.id;
                        setFormData(prev => ({
                          ...prev,
                          assistants: e.target.checked
                            ? [...(prev.assistants || []), userId]
                            : (prev.assistants || []).filter(id => id !== userId)
                        }));
                      }}
                      className="mb-2"
                    />
                  ))
                ) : (
                  <p className="text-muted text-center">لا يوجد مستخدمين متاحين</p>
                )}
              </div>
              <Form.Text className="text-muted">
                المساعدين: {formData.assistants?.length || 0} مستخدم
              </Form.Text>
            </Col>
            <Col md={12} className="mb-3">
              <Form.Label>وصف المشروع *</Form.Label>
              <Form.Control 
                as="textarea" 
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows={3} 
                placeholder="وصف المشروع" 
                required
              />
            </Col>
            <Col md={12} className="mb-3">
              <Form.Label>الأهداف *</Form.Label>
              <Form.Control 
                as="textarea" 
                name="objectives"
                value={formData.objectives}
                onChange={handleInputChange}
                rows={2} 
                placeholder="أهداف المشروع" 
                required
              />
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            إلغاء
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={saving}>
            {saving ? 'جاري الحفظ...' : 'حفظ'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* View Project Modal */}
      <Modal show={showViewModal} onHide={() => setShowViewModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>تفاصيل المشروع</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedProject && (
            <div>
              <Row>
                <Col md={6}>
                  <strong>كود المشروع:</strong> {selectedProject.project_code}
                </Col>
                <Col md={6}>
                  <strong>الحالة:</strong>{' '}
                  <Badge bg={getStatusBadge(selectedProject.status)}>
                    {getStatusText(selectedProject.status)}
                  </Badge>
                </Col>
              </Row>
              <hr />
              <Row>
                <Col md={12} className="mb-2">
                  <strong>اسم المشروع:</strong> {selectedProject.project_name}
                </Col>
                <Col md={6} className="mb-2">
                  <strong>العميل:</strong> {selectedProject.client_name || '-'}
                </Col>
                <Col md={6} className="mb-2">
                  <strong>النوع:</strong> {getTypeText(selectedProject.project_type)}
                </Col>
                <Col md={6} className="mb-2">
                  <strong>الميزانية:</strong> {formatCurrency(selectedProject.budget || 0, 'LYD')}
                </Col>
                <Col md={6} className="mb-2">
                  <strong>نسبة الإنجاز:</strong> {formatNumber(selectedProject.progress || 0)}%
                </Col>
                <Col md={6} className="mb-2">
                  <strong>تاريخ البداية:</strong> {selectedProject.start_date ? formatDate(selectedProject.start_date) : '-'}
                </Col>
                <Col md={6} className="mb-2">
                  <strong>تاريخ النهاية:</strong> {selectedProject.end_date ? formatDate(selectedProject.end_date) : '-'}
                </Col>
              </Row>
              <hr />
              <Row>
                <Col md={12}>
                  <strong>الوصف:</strong>
                  <p>{selectedProject.description || '-'}</p>
                </Col>
                <Col md={12}>
                  <strong>الأهداف:</strong>
                  <p>{selectedProject.objectives || '-'}</p>
                </Col>
              </Row>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowViewModal(false)}>
            إغلاق
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default ProjectList;
